import { Flex, Typography } from 'antd';
import Linkify from 'linkify-react';
import { useTranslation } from 'react-i18next';

import { displayValueOrNA } from '@/utils/displayValueOrNA';

import { wrapLabelWithLink, wrapWithTooltip } from './iconLabelShared';

import type React from 'react';
import type { ReactNode } from 'react';

type IconLabelTextProps = {
  icon?: ReactNode;
  label?: ReactNode;
  value?: ReactNode;
  /** When set, wraps the label in a link (router Link or external href). */
  link?: string;
  /** When set, wraps the item in an antd Tooltip with this label. */
  tooltipLabel?: string;
  className?: string;
};

const IconLabelText = ({
  icon,
  label,
  value,
  link,
  tooltipLabel,
  className,
}: IconLabelTextProps) => {
  const { t } = useTranslation();

  if (label === null) {
    return null;
  }

  const resolvedLabel = displayValueOrNA(label, t);
  const labelContent = wrapLabelWithLink(resolvedLabel, link);

  const valueContent =
    typeof value === 'string' ? (
      <Linkify
        options={{
          target: '_blank',
          rel: 'noopener noreferrer',
        }}
      >
        {value}
      </Linkify>
    ) : (
      value
    );

  const content: React.ReactNode = (
    <Flex align="start" gap="small" className={className}>
      <Flex style={{ paddingTop: 4 }}>{icon}</Flex>
      <Flex vertical gap={0} style={{ overflow: 'hidden' }}>
        <Typography.Text
          strong
          ellipsis={{ tooltip: typeof label === 'string' ? label : undefined }}
        >
          {labelContent}
        </Typography.Text>

        {value ? (
          <Typography.Text type="secondary">{valueContent}</Typography.Text>
        ) : null}
      </Flex>
    </Flex>
  );

  return wrapWithTooltip(content, tooltipLabel);
};

export default IconLabelText;
